import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Upload, FileText, Image, Loader2, ChevronLeft, BookOpen } from 'lucide-react';
import api from '@/utils/api';

const VendreLivresPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [boutiqueEbooks, setBoutiqueEbooks] = useState([]);
  const [loadingEbooks, setLoadingEbooks] = useState(false);

  const [formData, setFormData] = useState({
    titre: '',
    categorie: '',
    prix: '',
    date_sortie: '',
    description: '',
    pdf_file: null,
    couverture_file: null
  });

  const [pdfPreview, setPdfPreview] = useState(null);
  const [couverturePreview, setCouverturePreview] = useState(null);

  const categories = [
    'Roman',
    'Science-Fiction',
    'Fantasy',
    'Policier/Thriller',
    'Romance',
    'Histoire',
    'Biographie',
    'Éducation/Scolaire',
    'Business/Entrepreneuriat',
    'Développement Personnel',
    'Religion/Spiritualité',
    'Cuisine',
    'Art',
    'Poésie',
    'Jeunesse',
    'Bande Dessinée',
    'Autre'
  ];

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, navigate]);

  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('Seuls les fichiers PDF sont acceptés');
        return;
      }
      if (file.size > 50 * 1024 * 1024) { // 50MB max
        toast.error('Le fichier PDF ne doit pas dépasser 50MB');
        return;
      }
      setFormData({ ...formData, pdf_file: file });
      setPdfPreview(file.name);
    }
  };

  const handleCouvertureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
        toast.error('Seuls les fichiers JPG/PNG sont acceptés pour la couverture');
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB max
        toast.error('L\'image de couverture ne doit pas dépasser 10MB');
        return;
      }
      setFormData({ ...formData, couverture_file: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setCouverturePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Charger les ebooks de la boutique
  useEffect(() => {
    const fetchBoutiqueEbooks = async () => {
      setLoadingEbooks(true);
      try {
        const response = await api.get('/boutique-ebooks');
        setBoutiqueEbooks(response.data || []);
      } catch (error) {
        console.error('Erreur lors du chargement des ebooks:', error);
      } finally {
        setLoadingEbooks(false);
      }
    };

    fetchBoutiqueEbooks();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validations
    if (!formData.titre) {
      toast.error('Veuillez entrer le titre du livre');
      return;
    }
    if (!formData.categorie) {
      toast.error('Veuillez choisir une catégorie');
      return;
    }
    if (!formData.prix) {
      toast.error('Veuillez fixer le prix de votre livre');
      return;
    }
    if (parseFloat(formData.prix) < 0) {
      toast.error('Le prix ne peut pas être négatif');
      return;
    }
    if (!formData.date_sortie) {
      toast.error('Veuillez indiquer la date de sortie du livre');
      return;
    }
    if (!formData.pdf_file) {
      toast.error('Veuillez télécharger votre fichier PDF');
      return;
    }
    if (!formData.description) {
      toast.error('Veuillez entrer une description');
      return;
    }
    if (!formData.couverture_file) {
      toast.error('Veuillez télécharger une image de couverture');
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('titre', formData.titre);
      formDataToSend.append('categorie', formData.categorie);
      formDataToSend.append('prix', formData.prix);
      formDataToSend.append('date_sortie', formData.date_sortie);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('pdf', formData.pdf_file);
      formDataToSend.append('couverture', formData.couverture_file);

      const response = await api.post('/ebooks/vendre', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        toast.success('Livre envoyé avec succès ! Il sera visible dans la boutique.');
        // Reset form
        setFormData({
          titre: '',
          categorie: '',
          prix: '',
          date_sortie: '',
          description: '',
          pdf_file: null,
          couverture_file: null
        });
        setPdfPreview(null);
        setCouverturePreview(null);
        
        // Reload ebooks
        const ebooksResponse = await api.get('/boutique-ebooks');
        setBoutiqueEbooks(ebooksResponse.data || []);
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi du livre');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50">
      {/* Header */}
      <div className="border-b-4 border-yellow-300 bg-gradient-to-r from-yellow-100 via-amber-100 to-orange-100 shadow-lg sticky top-0 z-40 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-yellow-700 hover:text-yellow-900 transition-colors font-bold text-lg"
          >
            <ChevronLeft size={24} />
            <span>Retour</span>
          </button>
          <h1 className="text-2xl md:text-3xl font-black bg-gradient-to-r from-yellow-600 via-amber-600 to-orange-600 bg-clip-text text-transparent" style={{ fontFamily: 'Outfit' }}>
            📚 VENDRE MES LIVRES NUMÉRIQUES
          </h1>
          <div className="w-24"></div>
        </div>
      </div>

      <main className="pt-12 pb-20 max-w-4xl mx-auto px-4">
        {/* Titre avec dégradé éclatant */}
        <div className="text-center mb-8">
          <h2 className="text-5xl md:text-6xl font-black mb-4 bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-400 bg-clip-text text-transparent drop-shadow-lg" 
              style={{ textShadow: '0 0 60px rgba(251,191,36,0.3)' }}>
            📖 Publiez votre livre numérique
          </h2>
          <div className="h-2 w-64 mx-auto bg-gradient-to-r from-yellow-500 via-amber-500 to-orange-500 rounded-full shadow-lg"></div>
        </div>

        <Card className="bg-white/95 backdrop-blur-sm border-4 border-yellow-300 shadow-2xl shadow-yellow-500/30 relative">
          <CardHeader className="bg-gradient-to-r from-yellow-50 via-amber-50 to-orange-50 border-b-4 border-orange-300">
            <CardTitle className="text-yellow-900 text-3xl font-black flex items-center gap-3">
              <span className="text-4xl">📚</span>
              Mon Livre Numérique (PDF/Ebook)
            </CardTitle>
            <CardDescription className="text-yellow-700 text-lg font-semibold">
              Remplissez le formulaire ci-dessous pour publier votre livre sur AGRP BOX
            </CardDescription>
          </CardHeader>

          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* 1. Titre du livre */}
              <div className="p-6 bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-xl border-2 border-yellow-300">
                <Label htmlFor="titre" className="text-yellow-900 font-bold text-lg flex items-center gap-2">
                  <span className="text-2xl">1️⃣</span> Titre du livre *
                </Label>
                <Input
                  id="titre"
                  type="text"
                  placeholder="Ex: Mon Premier Roman"
                  value={formData.titre}
                  onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                  className="bg-white border-2 border-yellow-300 text-yellow-900 mt-3 text-lg font-semibold focus:border-yellow-500 focus:ring-yellow-500"
                />
              </div>

              {/* 2. Catégorie */}
              <div className="p-6 bg-gradient-to-r from-amber-50 to-amber-100 rounded-xl border-2 border-amber-300">
                <Label htmlFor="categorie" className="text-amber-900 font-bold text-lg flex items-center gap-2">
                  <span className="text-2xl">2️⃣</span> Catégorie / Genre *
                </Label>
                <select
                  id="categorie"
                  value={formData.categorie}
                  onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
                  className="w-full h-12 px-4 py-2 mt-3 bg-white border-2 border-amber-300 text-amber-900 font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 cursor-pointer text-lg"
                >
                  <option value="" disabled>Choisissez une catégorie</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat} className="bg-white text-amber-900">
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              {/* 3. Prix */}
              <div className="p-6 bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl border-2 border-orange-300">
                <Label htmlFor="prix" className="text-orange-900 font-bold text-lg flex items-center gap-2">
                  <span className="text-2xl">3️⃣</span> Prix de vente (en dollars USD) *
                </Label>
                <div className="relative mt-3">
                  <span className="absolute left-4 top-4 text-orange-600 font-black text-xl">$</span>
                  <Input
                    id="prix"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Ex: 10.00"
                    value={formData.prix}
                    onChange={(e) => setFormData({ ...formData, prix: e.target.value })}
                    className="bg-white border-2 border-orange-300 text-orange-900 pl-10 text-lg font-bold focus:border-orange-500 focus:ring-orange-500"
                  />
                </div>
                <p className="text-sm text-orange-700 mt-2 font-semibold flex items-center gap-2">
                  💰 Fixez le prix de votre livre numérique
                </p>
              </div>

              {/* 4. Date de sortie */}
              <div className="p-6 bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-xl border-2 border-yellow-400">
                <Label htmlFor="date_sortie" className="text-yellow-900 font-bold text-lg flex items-center gap-2">
                  <span className="text-2xl">4️⃣</span> Date de sortie du livre *
                </Label>
                <Input
                  id="date_sortie"
                  type="date"
                  value={formData.date_sortie}
                  onChange={(e) => setFormData({ ...formData, date_sortie: e.target.value })}
                  className="bg-white border-2 border-yellow-400 text-yellow-900 mt-3 text-lg font-bold focus:border-yellow-500 focus:ring-yellow-500"
                  required
                />
                <p className="text-sm text-yellow-700 mt-2 font-semibold flex items-center gap-2">
                  📅 Indiquez quand ce livre est sorti
                </p>
              </div>

              {/* 5. Upload PDF */}
              <div className="p-6 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border-2 border-amber-300">
                <Label htmlFor="pdf" className="text-amber-900 font-bold text-lg flex items-center gap-2">
                  <span className="text-2xl">5️⃣</span> Fichier PDF du livre * (Max 50MB)
                </Label>
                <div className="mt-3">
                  <label 
                    htmlFor="pdf-upload" 
                    className="flex items-center justify-center gap-4 bg-white border-4 border-dashed border-amber-400 hover:border-amber-600 rounded-2xl p-10 cursor-pointer transition-all hover:shadow-lg hover:shadow-amber-300"
                  >
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
                      <FileText size={36} className="text-white" />
                    </div>
                    <div className="text-center">
                      <p className="text-amber-900 font-bold text-lg">
                        {pdfPreview ? `✅ ${pdfPreview}` : '📄 Cliquez pour télécharger votre PDF'}
                      </p>
                      <p className="text-amber-600 text-sm font-semibold">Format: PDF (Max 50MB)</p>
                    </div>
                  </label>
                  <input
                    id="pdf-upload"
                    type="file"
                    accept="application/pdf"
                    onChange={handlePdfChange}
                    className="hidden"
                  />
                </div>
              </div>

              {/* 6. Description */}
              <div className="p-6 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl border-2 border-yellow-300">
                <Label htmlFor="description" className="text-yellow-900 font-bold text-lg flex items-center gap-2">
                  <span className="text-2xl">6️⃣</span> Description du livre *
                </Label>
                <Textarea
                  id="description"
                  placeholder="Décrivez votre livre : l'histoire, les thèmes, le public cible..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={5}
                  className="bg-white border-2 border-yellow-300 text-yellow-900 mt-3 text-lg font-medium focus:border-yellow-500 focus:ring-yellow-500"
                />
              </div>

              {/* 7. Image de couverture */}
              <div className="p-6 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl border-2 border-orange-300">
                <Label htmlFor="couverture" className="text-orange-900 font-bold text-lg flex items-center gap-2">
                  <span className="text-2xl">7️⃣</span> Image de couverture du livre * (JPG/PNG)
                </Label>
                <div className="mt-3">
                  <label 
                    htmlFor="couverture-upload" 
                    className="flex items-center justify-center gap-4 bg-white border-4 border-dashed border-orange-400 hover:border-orange-600 rounded-2xl p-10 cursor-pointer transition-all hover:shadow-lg hover:shadow-orange-300"
                  >
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center">
                      <Image size={36} className="text-white" />
                    </div>
                    <div className="text-center">
                      <p className="text-orange-900 font-bold text-lg">
                        {formData.couverture_file ? `✅ ${formData.couverture_file.name}` : '🖼️ Cliquez pour télécharger la couverture'}
                      </p>
                      <p className="text-orange-600 text-sm font-semibold">Formats: JPG, PNG</p>
                    </div>
                  </label>
                  <input
                    id="couverture-upload"
                    type="file"
                    accept="image/jpeg,image/png,image/jpg"
                    onChange={handleCouvertureChange}
                    className="hidden"
                  />
                  {couverturePreview && (
                    <img 
                      src={couverturePreview} 
                      alt="Couverture preview" 
                      className="mt-4 w-full max-h-64 object-contain rounded-xl border-4 border-orange-300 shadow-lg bg-white"
                    />
                  )}
                </div>
              </div>

              {/* Bouton Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-8 text-2xl font-black text-white rounded-2xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-2xl hover:shadow-yellow-500/50 relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #eab308 0%, #f59e0b 50%, #ea580c 100%)',
                }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-3">
                    <Loader2 className="animate-spin" size={32} />
                    ⏳ Envoi en cours...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-3">
                    <BookOpen size={32} />
                    🚀 PUBLIER MON LIVRE MAINTENANT
                  </span>
                )}
              </button>
            </form>
          </CardContent>
        </Card>

        {/* Section Galerie des Livres Publiés */}
        <div className="mt-16 bg-white/95 backdrop-blur-sm rounded-3xl p-8 border-4 border-yellow-300 shadow-2xl">
          <div className="text-center mb-8">
            <h2 className="text-4xl md:text-5xl font-black mb-4 bg-gradient-to-r from-yellow-600 via-amber-600 to-orange-600 bg-clip-text text-transparent drop-shadow-lg">
              📚 Livres Déjà Publiés
            </h2>
            <div className="h-1 w-48 mx-auto bg-gradient-to-r from-yellow-500 via-amber-500 to-orange-500 rounded-full"></div>
          </div>

          {loadingEbooks ? (
            <div className="text-center py-12">
              <Loader2 className="animate-spin mx-auto mb-4 text-yellow-500" size={48} />
              <p className="text-yellow-400 font-semibold">Chargement des livres...</p>
            </div>
          ) : boutiqueEbooks.length === 0 ? (
            <Card className="bg-white/95 border-4 border-yellow-300 p-12 text-center">
              <BookOpen size={64} className="mx-auto mb-4 text-yellow-500" />
              <p className="text-yellow-900 font-bold text-xl">Aucun livre publié pour le moment</p>
              <p className="text-yellow-700 mt-2">Soyez le premier à publier !</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {boutiqueEbooks.map((ebook) => (
                <Card 
                  key={ebook.id} 
                  className="bg-white/95 border-4 border-yellow-300 hover:border-amber-400 transition-all hover:scale-105 hover:shadow-xl hover:shadow-yellow-500/50 cursor-pointer overflow-hidden"
                  onClick={() => navigate(`/boutique-ebooks`)}
                >
                  <div className="relative">
                    {ebook.couverture_path ? (
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/storage/${ebook.couverture_path}`}
                        alt={ebook.titre}
                        className="w-full h-64 object-cover"
                      />
                    ) : (
                      <div className="w-full h-64 bg-gradient-to-br from-yellow-500 to-amber-500 flex items-center justify-center">
                        <BookOpen size={64} className="text-white" />
                      </div>
                    )}
                    <div className="absolute top-3 right-3 bg-gradient-to-r from-yellow-600 to-amber-600 text-white px-3 py-1 rounded-full font-bold text-sm shadow-lg">
                      ${ebook.prix}
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-black text-yellow-900 text-lg mb-2 line-clamp-1">
                      {ebook.titre}
                    </h3>
                    <p className="text-amber-700 font-semibold text-sm mb-3 line-clamp-2">
                      {ebook.description}
                    </p>
                    {ebook.date_sortie && (
                      <div className="mb-2">
                        <span className="text-xs text-orange-600 font-bold bg-orange-100 px-2 py-1 rounded">
                          📅 Sortie : {new Date(ebook.date_sortie).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-xs text-yellow-600 font-semibold">
                      <span>👤 {ebook.auteur_nom || ebook.user_name}</span>
                      <span className="flex items-center gap-1">
                        💾 {ebook.telecharges || 0}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default VendreLivresPage;
